import { useState, useEffect } from 'react';
import { getLoginLogs } from './api';

export const CHANGELOG = [
  {
    version: 'v2.5',
    date: '20.05.2026',
    title: 'Fleet Management Module & Report Tracker Enhancements',
    items: [
      'Fleet Management extracted as standalone module from VSAD Generator',
      'Vessel CRUD, CSV/Excel import and data completeness indicator moved to dedicated Fleet Management module',
      'VSAD Generator streamlined: Fleet Database tab removed',
      'Report Tracker: MSO Review Date tracking added per vessel report',
      'Report Tracker: dual notification flows for deadline and MSO review reminders',
      'Report Tracker: color-coded MSO status badges (REVIEWED / PENDING / OVERDUE)',
      'Report Tracker: admin and user have identical access rights',
      'Module access control updated for Fleet Management and Report Tracker',
    ],
  },
  {
    version: 'v2.4',
    date: '18.05.2026',
    title: 'LPSQ Report Tracker — Compliance Deadline Management',
    items: [
      'New LPSQ Report Tracker module: manage recurring compliance reports per vessel',
      'Dynamic tabs: one tab per report type (configurable by admin)',
      'Per-type dashboard: total / on track / due soon / urgent/overdue counters',
      'Vessel report table: completion date, confirmed date, auto-calculated deadline, days remaining, color-coded status',
      'Status logic: green (>30d), yellow (8–30d), red (≤7d or overdue)',
      'Add/Edit modal: select vessel, enter completion/confirmed dates, auto-deadline preview',
      'Admin: Report Types management — create/edit/delete types with interval, notification thresholds, color',
      'Gmail SMTP settings: notification email, sender address, app password (stored in DB)',
      'Test mail button: verify credentials before going live',
      'APScheduler daily job at 08:00 UTC: checks deadlines, sends first/second email reminders',
      'Notification log: every send attempt recorded with status and error details',
      'Notification flags reset automatically when deadline changes',
    ],
  },
  {
    version: 'v2.3',
    date: '28.03.2026',
    title: 'Fleet Database — Bulk Import & Data Quality',
    items: [
      'Vessel management moved from Dashboard into VSAD Generator → Fleet Database tab',
      'Bulk import: CSV and .xlsx upload support — 100 vessels in one file',
      'Download template: pre-formatted CSV with all 18 ship particulars columns + example row',
      'Overwrite toggle: choose to update existing vessels or skip duplicates on import',
      'Import result summary: imported / updated / error counts with per-row error details',
      'Data quality indicator: completeness % per vessel (green ≥100%, gold ≥70%, red <70%)',
      'Required fields for VSAD: ship_name, imo_number, flag, ship_type, gross_tonnage, class_register, owner_manager',
      'Missing field warning shown in Generate tab when selected vessel has incomplete data',
      'Quick Generate: ⚡ button on each fleet row — switches to Generate tab with vessel pre-selected',
      'Dashboard cleaned up: now shows only module cards (no vessel list)',
    ],
  },
  {
    version: 'v2.2',
    date: '28.03.2026',
    title: 'Security Hardening — Authentication & Platform Security',
    items: [
      'HTTPS enabled with TLS 1.2/1.3 — all traffic now encrypted (self-signed certificate)',
      'HTTP → HTTPS automatic redirect via nginx',
      'Account lockout: 5 failed attempts locks account for 15 minutes',
      'IP-based rate limiting: 10 login attempts per minute per IP (DB-backed, survives restarts)',
      'Token blacklist: logout immediately invalidates JWT server-side',
      'Token expiry reduced from 8 hours to 2 hours',
      'Session expired banner shown on login page after token timeout',
      'Password policy enforced: min 8 chars, uppercase, lowercase, digit required',
      'Login audit trail: every attempt logged with IP, username, result and reason',
      'Login Logs panel added to admin header with new-entry notification badge',
      'Disabled accounts blocked at login and on every API request',
      'Generic error messages on login — no technical details exposed',
      'CORS restricted to platform IP only',
      'SECRET_KEY and DATABASE_URL are now required env variables — no hardcoded defaults',
    ],
  },
  {
    version: 'v2.1',
    date: '24.03.2026',
    title: 'PSC Guard — AI-Powered PSC Deficiency Analyzer',
    items: [
      'New PSC Guard module: upload PSC deficiency Excel and get a prioritized pre-inspection checklist',
      'Supports Paris MOU, Tokyo MOU, USCG and internal company deficiency report formats',
      'AI agent classifies each deficiency by ISM / ISPS / MLC / SOLAS / MARPOL / STCW',
      'Pattern recognition: recurring deficiencies are grouped and ranked by occurrence frequency',
      'Synthesizer agent builds a consolidated checklist with verification methods and responsible parties',
      'Download full checklist as a styled 3-sheet Excel (Checklist, Summary, Full Analysis)',
      'Backend: POST /psc-guard/analyze, GET /psc-guard/progress/{job_id}, GET /psc-guard/download/{file}',
    ],
  },
  {
    version: 'v2.0',
    date: '24.03.2026',
    title: 'BlameCatcher™ — Whose Job Is This Anyway?',
    items: [
      'New BlameCatcher module: AI agent that identifies who is responsible for any task',
      'Powered by IMS General #14 — Job Description & Roles and Responsibilities',
      'Witty + diplomatic persona: BlameCatcher reads 400+ pages so you don\'t have to',
      'Structured response: WHO\'S ON THE HOOK, WHAT THEY MUST DO, FINE PRINT, VERDICT',
      'Case history sidebar: keeps last 5 investigations for quick reference',
      'Animated radar scanner UI with cycling investigative loading messages',
      'Backend: POST /blamecatcher/ask, GET /blamecatcher/status endpoints',
    ],
  },
  {
    version: 'v1.9',
    date: '24.03.2026',
    title: 'Email Assistant — AI-Powered Email Action Item Extractor',
    items: [
      'New Email Assistant module: upload Power Automate Excel file, Claude AI extracts action items',
      'Filters only "To Review" rows; strips HTML from Outlook email bodies automatically',
      'Each task includes: name, priority, category, vessel, due date, key people, follow-up details',
      'One-click or bulk send to Notion Task Management database with rate limiting (350ms delay)',
      'Per-task Notion send status with URL links; error isolation — one failure does not stop others',
      'Backend: POST /email-assistant/preview, /analyze, /send-to-notion endpoints',
    ],
  },
  {
    version: 'v1.8',
    date: '23.03.2026',
    title: 'Vessel Audit Management — 4-Agent Expert Analysis System',
    items: [
      'Renamed Audit Assistant → Vessel Audit Management',
      '4 parallel specialist agents: ISM Auditor, ISPS Auditor, MLC Auditor, ISO Auditor (ISO 9001/14001)',
      'Each agent is domain-expert with deep knowledge of their specific code and supporting regulations',
      'Agents run in parallel (claude-opus-4-6) and outputs merged into a Final Audit Finding',
      'SSE Shore-010 v15 questionnaire auto-matching with Direct/Partial classification',
      'CA/PA items are countable & measurable with specific evidence requirements',
      'Photo upload for each CA/PA item — vessel uploads evidence for office verification',
      'Findings persisted with unique IDs; evidence photos stored server-side',
      'Library search integration — company procedures referenced during analysis',
    ],
  },
  {
    version: 'v1.7',
    date: '22.03.2026',
    title: 'QDMS Quality Analyzer — AI Document Quality Scanner',
    items: [
      'New QDMS Analyzer module: scans all IMS documents simultaneously with Claude AI',
      '6 analysis types: Typos, Terminology, Conflicts, Cross-References, Outdated Regulations, Format',
      'Severity levels: CRITICAL (compliance risk) and IMPROVEMENT (best practice)',
      'Findings grouped by document with color-coded badges',
      'Download PDF report: professional layout with executive summary and per-document findings',
      'Backend: POST /qdms-analyzer/analyze, GET /qdms-analyzer/report/{filename} endpoints',
    ],
  },
  {
    version: 'v1.6',
    date: '21.03.2026',
    title: 'IMS Library — Dedicated Management Module',
    items: [
      'New IMS Manager module with structured document slots (INDEX + 13 GENERAL + 12 SHORE)',
      'Each slot is pre-defined by number and title; shows upload status, filename, date and uploader',
      'Admin-only upload (overwrite), delete and reindex; all users can view',
      'Backend: /ims/structure, /ims/upload, /ims/files, /ims/reindex endpoints added',
      'ChromaDB indexing with ims_general / ims_shore / ims_index category tags',
    ],
  },
  {
    version: 'v1.5',
    date: '20.03.2026',
    title: 'User Management Relocated & Logbook Added',
    items: [
      'User Management button moved from main module cards to header (next to Architecture)',
      'HISTORY button added to header — platform changelog displayed as a popup',
    ],
  },
  {
    version: 'v1.4',
    date: '20.03.2026',
    title: 'Library — ISO Categories Added',
    items: [
      'ISO 9001:2015 (Quality Management System) category added to Library Manager',
      'ISO 14001:2015 (Environmental Management System) category added to Library Manager',
      'Backend and frontend updated simultaneously; upload, delete and reindex all functional',
    ],
  },
  {
    version: 'v1.3',
    date: '20.03.2026',
    title: 'Library — File Count Bug Fixed',
    items: [
      'Left sidebar category counters now show actual file count instead of indexed chunk count',
      'Total file count in the header corrected accordingly',
    ],
  },
  {
    version: 'v1.2',
    date: '20.03.2026',
    title: 'VSAD — GMP_ANN File Names Updated',
    items: [
      'GMP_ANN templates renamed from GMP_ANN_01–05.docx to Annex_3–Annex_8.docx',
      'File count updated from 5 to 6',
    ],
  },
  {
    version: 'v1.1',
    date: '20.03.2026',
    title: 'VSAD — Template Management Screen',
    items: [
      'TEMPLATES tab added to VSAD Generator',
      'Template files listed per plan with status (uploaded/missing), size and date',
      'Individual Upload and Replace buttons per .docx file',
      'Backend: GET /vsad/templates and POST /vsad/templates/upload endpoints added',
    ],
  },
  {
    version: 'v1.0',
    date: '20.03.2026',
    title: 'Platform Initial Setup',
    items: [
      'FastAPI backend, React 19 frontend, PostgreSQL database setup',
      'Vessel management (CRUD), VSAD document generator, Library Manager, Audit Assistant modules',
      'JWT authentication, ChromaDB vector search, LibreOffice PDF conversion',
    ],
  },
];

export default function Dashboard({ onLogout, setPage, allowedModules = [], isAdmin = false }) {
  const [showArch, setShowArch] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showCollab, setShowCollab] = useState(false);
  const [showLoginLogs, setShowLoginLogs] = useState(false);
  const [loginLogs, setLoginLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [failedLogsCount, setFailedLogsCount] = useState(0);
  const [hoveredModule, setHoveredModule] = useState(null);

  useEffect(() => {
    if (!isAdmin) return;
    getLoginLogs(200).then(r => {
      const lastSeen = parseInt(localStorage.getItem('loginLogsLastSeen') || '0', 10);
      const newCount = (r.data || []).filter(l => new Date(l.created_at).getTime() > lastSeen).length;
      setFailedLogsCount(newCount);
    }).catch(() => {});
  }, [isAdmin]);

  const modules = [
    { key: 'vsad',           icon: '📄', name: "VSAD Generator",          desc: "Generate ship management plans",    accent: '#e8001d' },
    { key: 'audit',          icon: '⚓', name: "Vessel Audit Management",  desc: "4-Agent Expert Finding Analysis",  accent: '#e07b2a' },
    { key: 'ims',            icon: '🗂️', name: "QDMS",                     desc: "Quality Document Management",      accent: '#3b82f6' },
    { key: 'qdms-analyzer',  icon: '🧠', name: "QDMS Analyzer",            desc: "AI document quality scan",         accent: '#a855f7' },
    { key: 'library',        icon: '📚', name: "Library",                  desc: "Document management",              accent: '#22c55e' },
    { key: 'email-assistant',icon: '📧', name: "Email Assistant",          desc: "AI email action item extractor",   accent: '#14b8a6' },
    { key: 'blamecatcher',   icon: '🕵️', name: "BlameCatcher™",            desc: "Whose Job Is This Anyway?",         accent: '#e8001d' },
    { key: 'psc-guard',      icon: '⚓', name: "PSC Guard",                desc: "Deficiency analyzer & checklist",   accent: '#c9a84c' },
    { key: 'brainstorm',      icon: '🧠', name: "BrainStorm™",              desc: "Multi-agent expert panel analysis",   accent: '#7c3aed' },
    { key: 'report-tracker',   icon: '📋', name: "LPSQ Report Tracker",  desc: "Compliance report deadlines & reminders", accent: '#06b6d4' },
    { key: 'fleet-management', icon: '🚢', name: "Fleet Management",      desc: "Vessel database & fleet operations",       accent: '#e8001d' },
  ];

  const visibleModules = isAdmin ? modules : modules.filter(m => allowedModules.includes(m.key));
  const currentVersion = CHANGELOG[0]?.version || 'v1.0';

  return (
    <div style={styles.wrapper}>
      <div style={styles.topAccent} />
      <header style={styles.header}>
        <div style={styles.headerLeft}>
          <div style={styles.logoIcon}>MP</div>
          <div>
            <div style={styles.logoTitle}>CSH - QSSE Department</div>
            <div style={styles.logoSub}>MARITIME PLATFORM</div>
          </div>
          <button style={styles.versionBadge} onClick={() => setShowHistory(true)}>
            {currentVersion}
          </button>
        </div>
        <div style={styles.headerRight}>
          {isAdmin && (
            <>
              <div style={styles.headerBtnGroup}>
                <button style={styles.archBtn} onClick={() => setShowHistory(true)}>HISTORY</button>
                <button style={styles.archBtn} onClick={() => setShowArch(true)}>ARCHITECTURE</button>
                <button style={styles.collabHeaderBtn} onClick={() => setShowCollab(true)}>⬡ AGENT COLLABORATION</button>
              </div>
              <div style={styles.headerDivider} />
              <button style={styles.archBtn} onClick={() => setPage('users')}>USER MANAGEMENT</button>
              <div style={{ position: 'relative', display: 'inline-flex' }}>
                <button style={styles.logsBtn} onClick={async () => {
                  setShowLoginLogs(true);
                  setLogsLoading(true);
                  try { const r = await getLoginLogs(); setLoginLogs(r.data); localStorage.setItem('loginLogsLastSeen', Date.now().toString()); setFailedLogsCount(0); }
                  catch { setLoginLogs([]); }
                  finally { setLogsLoading(false); }
                }}>🔐 LOGIN LOGS</button>
                {failedLogsCount > 0 && (
                  <span style={styles.logsBadge}>{failedLogsCount > 99 ? '99+' : failedLogsCount}</span>
                )}
              </div>
            </>
          )}
          <button style={styles.logoutBtn} onClick={onLogout}>LOGOUT</button>
        </div>
      </header>

      <main style={styles.main}>
        <div style={styles.modulesGrid}>
          {visibleModules.map(m => (
            <div
              key={m.key}
              style={{
                ...styles.moduleCard,
                borderLeftColor: m.accent,
                boxShadow: hoveredModule === m.key
                  ? `0 0 0 1px ${m.accent}55, inset 0 0 20px ${m.accent}08`
                  : 'none',
                borderColor: hoveredModule === m.key ? `${m.accent}55` : 'var(--navy-border)',
              }}
              onClick={() => setPage(m.key)}
              onMouseEnter={() => setHoveredModule(m.key)}
              onMouseLeave={() => setHoveredModule(null)}
            >
              <div style={styles.moduleIcon}>{m.icon}</div>
              <div style={styles.moduleName}>{m.name}</div>
              <div style={styles.moduleDesc}>{m.desc}</div>
            </div>
          ))}
        </div>

      </main>
      {showHistory && (
        <div style={styles.archOverlay} onClick={() => setShowHistory(false)}>
          <div style={styles.historyModal} onClick={e => e.stopPropagation()}>
            <div style={styles.historyHeader}>
              <div>
                <div style={styles.historyTitle}>LOGBOOK</div>
                <div style={styles.historySub}>Platform change history</div>
              </div>
              <button style={styles.archClose} onClick={() => setShowHistory(false)}>✕ CLOSE</button>
            </div>
            <div style={styles.historyBody}>
              {CHANGELOG.map((entry, i) => (
                <div key={i} style={styles.historyEntry}>
                  <div style={styles.historyEntryLeft}>
                    <div style={styles.historyVersion}>{entry.version}</div>
                    <div style={styles.historyDate}>{entry.date}</div>
                  </div>
                  <div style={styles.historyEntryRight}>
                    <div style={styles.historyEntryTitle}>{entry.title}</div>
                    <ul style={styles.historyList}>
                      {entry.items.map((item, j) => (
                        <li key={j} style={styles.historyItem}>{item}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {showArch && (
        <div style={styles.archOverlay} onClick={() => setShowArch(false)}>
          <div style={styles.archModal} onClick={e => e.stopPropagation()}>
            <button style={styles.archClose} onClick={() => setShowArch(false)}>✕ CLOSE</button>
            <iframe
              src="/architecture-diagram.html"
              style={styles.archFrame}
              title="Architecture Diagram"
            />
          </div>
        </div>
      )}

      {/* ── Agent Collaboration Modal ── */}
      {showCollab && (
        <div style={styles.archOverlay} onClick={() => setShowCollab(false)}>
          <div style={styles.collabModal} onClick={e => e.stopPropagation()}>
            <div style={styles.collabModalHeader}>
              <div>
                <div style={styles.collabModalTitle}>AGENT COLLABORATION</div>
                <div style={styles.collabModalSub}>How AI agents work together across modules</div>
              </div>
              <button style={styles.archClose} onClick={() => setShowCollab(false)}>✕ CLOSE</button>
            </div>
            <div style={styles.collabModalBody}>
              <div style={styles.collabGrid}>

                {/* Workflow 1 */}
                <div style={styles.collabCard}>
                  <div style={styles.collabCardLabel}>PSC INSPECTION PREP</div>
                  <div style={styles.collabFlow}>
                    <div style={styles.collabNode}>
                      <div style={{ ...styles.collabNodeIcon, background: 'rgba(201,168,76,0.12)', borderColor: 'rgba(201,168,76,0.3)' }}>⚓</div>
                      <div style={styles.collabNodeName}>PSC Guard</div>
                      <div style={styles.collabNodeDesc}>Upload deficiency Excel → priority checklist</div>
                    </div>
                    <div style={styles.collabArrow}>→</div>
                    <div style={styles.collabNode}>
                      <div style={{ ...styles.collabNodeIcon, background: 'rgba(224,123,42,0.12)', borderColor: 'rgba(224,123,42,0.3)' }}>⚓</div>
                      <div style={styles.collabNodeName}>Audit Mgmt</div>
                      <div style={styles.collabNodeDesc}>Analyze findings by ISM / ISPS / MLC / ISO</div>
                    </div>
                    <div style={styles.collabArrow}>→</div>
                    <div style={styles.collabNode}>
                      <div style={{ ...styles.collabNodeIcon, background: 'rgba(232,0,29,0.10)', borderColor: 'rgba(232,0,29,0.25)' }}>🕵️</div>
                      <div style={styles.collabNodeName}>BlameCatcher</div>
                      <div style={styles.collabNodeDesc}>Identify responsible officer per finding</div>
                    </div>
                  </div>
                  <div style={styles.collabDesc}>
                    Start with PSC Guard to classify historical deficiencies, pass critical items to Audit
                    Management for root-cause analysis, then use BlameCatcher to assign accountability
                    to the correct officer per the Job Description manual.
                  </div>
                </div>

                {/* Workflow 2 */}
                <div style={styles.collabCard}>
                  <div style={styles.collabCardLabel}>DOCUMENT QUALITY CYCLE</div>
                  <div style={styles.collabFlow}>
                    <div style={styles.collabNode}>
                      <div style={{ ...styles.collabNodeIcon, background: 'rgba(59,130,246,0.12)', borderColor: 'rgba(59,130,246,0.3)' }}>🗂️</div>
                      <div style={styles.collabNodeName}>QDMS / IMS</div>
                      <div style={styles.collabNodeDesc}>Upload manuals & procedures to IMS slots</div>
                    </div>
                    <div style={styles.collabArrow}>→</div>
                    <div style={styles.collabNode}>
                      <div style={{ ...styles.collabNodeIcon, background: 'rgba(168,85,247,0.12)', borderColor: 'rgba(168,85,247,0.3)' }}>🧠</div>
                      <div style={styles.collabNodeName}>QDMS Analyzer</div>
                      <div style={styles.collabNodeDesc}>Scan all docs for conflicts & outdated regs</div>
                    </div>
                    <div style={styles.collabArrow}>→</div>
                    <div style={styles.collabNode}>
                      <div style={{ ...styles.collabNodeIcon, background: 'rgba(232,0,29,0.10)', borderColor: 'rgba(232,0,29,0.25)' }}>🕵️</div>
                      <div style={styles.collabNodeName}>BlameCatcher</div>
                      <div style={styles.collabNodeDesc}>Who must fix the identified gap?</div>
                    </div>
                  </div>
                  <div style={styles.collabDesc}>
                    Upload documents to IMS Manager, run QDMS Analyzer to detect quality issues across
                    the entire corpus, then ask BlameCatcher who is responsible for correcting each
                    identified gap — all grounded in the actual document content.
                  </div>
                </div>

                {/* Workflow 3 */}
                <div style={styles.collabCard}>
                  <div style={styles.collabCardLabel}>EMAIL-TO-ACTION PIPELINE</div>
                  <div style={styles.collabFlow}>
                    <div style={styles.collabNode}>
                      <div style={{ ...styles.collabNodeIcon, background: 'rgba(20,184,166,0.12)', borderColor: 'rgba(20,184,166,0.3)' }}>📧</div>
                      <div style={styles.collabNodeName}>Email Assistant</div>
                      <div style={styles.collabNodeDesc}>Extract action items from Outlook emails</div>
                    </div>
                    <div style={styles.collabArrow}>→</div>
                    <div style={styles.collabNode}>
                      <div style={{ ...styles.collabNodeIcon, background: 'rgba(232,0,29,0.10)', borderColor: 'rgba(232,0,29,0.25)' }}>🕵️</div>
                      <div style={styles.collabNodeName}>BlameCatcher</div>
                      <div style={styles.collabNodeDesc}>Confirm assigned officer per IMS #14</div>
                    </div>
                    <div style={styles.collabArrow}>→</div>
                    <div style={styles.collabNode}>
                      <div style={{ ...styles.collabNodeIcon, background: 'rgba(100,100,200,0.12)', borderColor: 'rgba(100,100,200,0.3)' }}>📋</div>
                      <div style={styles.collabNodeName}>Notion</div>
                      <div style={styles.collabNodeDesc}>Tasks pushed to task management database</div>
                    </div>
                  </div>
                  <div style={styles.collabDesc}>
                    Email Assistant extracts tasks from Power Automate email exports. Before sending
                    to Notion, verify the correct responsible party with BlameCatcher. Final tasks
                    are pushed directly to the Notion task management database with one click.
                  </div>
                </div>

                {/* Workflow 4 */}
                <div style={styles.collabCard}>
                  <div style={styles.collabCardLabel}>AUDIT PREPARATION</div>
                  <div style={styles.collabFlow}>
                    <div style={styles.collabNode}>
                      <div style={{ ...styles.collabNodeIcon, background: 'rgba(34,197,94,0.12)', borderColor: 'rgba(34,197,94,0.3)' }}>📚</div>
                      <div style={styles.collabNodeName}>Library</div>
                      <div style={styles.collabNodeDesc}>Index ISM, ISPS, MLC, ISO procedures</div>
                    </div>
                    <div style={styles.collabArrow}>→</div>
                    <div style={styles.collabNode}>
                      <div style={{ ...styles.collabNodeIcon, background: 'rgba(224,123,42,0.12)', borderColor: 'rgba(224,123,42,0.3)' }}>⚓</div>
                      <div style={styles.collabNodeName}>Audit Mgmt</div>
                      <div style={styles.collabNodeDesc}>AI searches library during analysis</div>
                    </div>
                    <div style={styles.collabArrow}>→</div>
                    <div style={styles.collabNode}>
                      <div style={{ ...styles.collabNodeIcon, background: 'rgba(201,168,76,0.12)', borderColor: 'rgba(201,168,76,0.3)' }}>📄</div>
                      <div style={styles.collabNodeName}>VSAD Generator</div>
                      <div style={styles.collabNodeDesc}>Generate management plans pre-inspection</div>
                    </div>
                  </div>
                  <div style={styles.collabDesc}>
                    Upload company procedures to Library so Audit Management agents can reference them
                    during finding analysis. Use VSAD Generator to produce up-to-date ship management
                    plans (GMP, SOPEP, etc.) before an upcoming flag-state or port state inspection.
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>
      )}
      {showLoginLogs && (
        <div style={styles.archOverlay} onClick={() => setShowLoginLogs(false)}>
          <div style={styles.logsModal} onClick={e => e.stopPropagation()}>
            <div style={styles.historyHeader}>
              <div>
                <div style={styles.historyTitle}>LOGIN LOGS</div>
                <div style={styles.historySub}>Last 200 authentication attempts</div>
              </div>
              <button style={styles.archClose} onClick={() => setShowLoginLogs(false)}>✕ CLOSE</button>
            </div>
            <div style={styles.logsBody}>
              {logsLoading ? (
                <div style={{ color: 'var(--grey)', padding: '32px', textAlign: 'center' }}>Loading...</div>
              ) : loginLogs.length === 0 ? (
                <div style={{ color: 'var(--grey)', padding: '32px', textAlign: 'center' }}>No logs found.</div>
              ) : (
                <table style={styles.logsTable}>
                  <thead>
                    <tr>
                      {['TIME', 'USERNAME', 'IP ADDRESS', 'RESULT', 'REASON'].map(h => (
                        <th key={h} style={styles.logsTh}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {loginLogs.map((log, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid var(--navy-border)' }}>
                        <td style={styles.logsTd}>{new Date(log.created_at).toLocaleString()}</td>
                        <td style={{ ...styles.logsTd, fontFamily: 'var(--font-mono)' }}>{log.username}</td>
                        <td style={{ ...styles.logsTd, fontFamily: 'var(--font-mono)' }}>{log.ip_address}</td>
                        <td style={styles.logsTd}>
                          <span style={{
                            padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '700',
                            background: log.success ? 'rgba(34,197,94,0.12)' : 'rgba(232,0,29,0.12)',
                            color: log.success ? '#22c55e' : '#ff6b7a',
                            border: `1px solid ${log.success ? 'rgba(34,197,94,0.3)' : 'rgba(232,0,29,0.3)'}`,
                          }}>
                            {log.success ? 'SUCCESS' : 'FAILED'}
                          </span>
                        </td>
                        <td style={{ ...styles.logsTd, color: 'var(--grey)', fontSize: '12px' }}>{log.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  wrapper: { minHeight: '100vh', background: 'var(--navy)' },
  topAccent: {
    height: '3px',
    background: 'linear-gradient(90deg, var(--red) 0%, #c9a84c 60%, var(--red) 100%)',
  },
  header: {
    background: 'var(--navy-mid)', borderBottom: '1px solid var(--navy-border)',
    padding: '0 40px', height: '64px', display: 'flex',
    alignItems: 'center', justifyContent: 'space-between',
  },
  headerLeft: { display: 'flex', alignItems: 'center', gap: '14px' },
  versionBadge: {
    background: 'rgba(201,168,76,0.12)', border: '1px solid rgba(201,168,76,0.35)',
    color: '#c9a84c', padding: '3px 10px', borderRadius: '20px',
    fontSize: '11px', fontWeight: '700', letterSpacing: '0.5px',
    cursor: 'pointer', fontFamily: 'var(--font-mono)',
  },
  headerBtnGroup: { display: 'flex', gap: '8px' },
  headerDivider: {
    width: '1px', height: '20px', background: 'var(--navy-border)',
  },
  logoIcon: {
    width: '36px', height: '36px', background: 'var(--red)', borderRadius: '8px',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontFamily: 'var(--font-mono)', fontSize: '12px', fontWeight: '500', color: 'white',
  },
  logoTitle: { fontSize: '15px', fontWeight: '700' },
  logoSub: { fontSize: '10px', color: 'var(--grey)', letterSpacing: '2px', fontFamily: 'var(--font-mono)' },
  headerRight: { display: 'flex', alignItems: 'center', gap: '16px' },
  logsBtn: {
    background: 'rgba(232,0,29,0.08)', border: '1px solid rgba(232,0,29,0.25)',
    color: '#ff6b7a', padding: '6px 14px', borderRadius: '6px',
    fontSize: '11px', letterSpacing: '1px', cursor: 'pointer', fontFamily: 'var(--font-mono)',
  },
  logsBadge: {
    position: 'absolute', top: '-7px', right: '-7px',
    background: '#e8001d', color: 'white',
    borderRadius: '50%', minWidth: '18px', height: '18px',
    fontSize: '10px', fontWeight: '800', fontFamily: 'var(--font-mono)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '0 3px', pointerEvents: 'none',
    boxShadow: '0 0 0 2px var(--navy-mid)',
  },
  logsModal: {
    background: 'var(--navy-mid)', border: '1px solid var(--navy-border)',
    borderRadius: '16px', width: '90%', maxWidth: '900px',
    maxHeight: '80vh', display: 'flex', flexDirection: 'column', overflow: 'hidden',
  },
  logsBody: { overflowY: 'auto', flex: 1 },
  logsTable: { width: '100%', borderCollapse: 'collapse' },
  logsTh: {
    padding: '10px 16px', textAlign: 'left', fontSize: '10px', fontWeight: '700',
    letterSpacing: '1.5px', color: 'var(--grey)', fontFamily: 'var(--font-mono)',
    borderBottom: '1px solid var(--navy-border)', background: 'var(--navy)',
    position: 'sticky', top: 0,
  },
  logsTd: { padding: '10px 16px', fontSize: '13px', color: 'var(--white)', verticalAlign: 'middle' },
  logoutBtn: {
    background: 'transparent', border: '1px solid var(--navy-light)',
    color: 'var(--grey)', padding: '6px 16px', borderRadius: '6px',
    fontSize: '11px', letterSpacing: '1px', cursor: 'pointer', fontFamily: 'var(--font-mono)',
  },
  main: { padding: '40px', maxWidth: '1200px', margin: '0 auto' },
  topBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '32px' },
  pageTitle: { fontSize: '28px', fontWeight: '800', color: 'var(--white)' },
  pageSub: { fontSize: '13px', color: 'var(--grey)', marginTop: '4px' },
  topBarRight: { display: 'flex', gap: '12px', alignItems: 'center' },
  searchInput: {
    background: 'var(--navy-mid)', border: '1px solid var(--navy-light)',
    borderRadius: '8px', padding: '10px 16px', color: 'var(--white)',
    fontSize: '13px', outline: 'none', width: '260px', fontFamily: 'var(--font-mono)',
  },
  addBtn: {
    background: 'var(--red)', border: 'none', borderRadius: '8px',
    padding: '10px 20px', color: 'white', fontSize: '12px',
    fontWeight: '700', letterSpacing: '1px', cursor: 'pointer',
  },
  modulesGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '16px', marginBottom: '32px',
  },
  moduleCard: {
    background: 'var(--navy-mid)', border: '1px solid var(--navy-border)',
    borderLeft: '3px solid transparent',
    borderRadius: '12px', padding: '20px 24px', cursor: 'pointer',
    transition: 'border-color 0.2s, box-shadow 0.2s',
  },
  moduleIcon: { fontSize: '24px', marginBottom: '8px' },
  moduleName: { fontSize: '15px', fontWeight: '700', marginBottom: '4px' },
  moduleDesc: { fontSize: '12px', color: 'var(--grey)' },
  statsBar: {
    display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '16px', marginBottom: '24px',
  },
  statCard: {
    background: 'var(--navy-mid)', border: '1px solid var(--navy-border)',
    borderRadius: '10px', padding: '16px 24px',
  },
  statValue: {
    fontSize: '28px', fontWeight: '800', color: '#c9a84c',
    fontFamily: 'var(--font-mono)', lineHeight: 1,
  },
  statLabel: {
    fontSize: '10px', color: 'var(--grey)', letterSpacing: '1.5px',
    fontFamily: 'var(--font-mono)', marginTop: '6px',
  },
  statDetail: {
    fontSize: '11px', color: 'var(--grey)', marginTop: '4px',
    opacity: 0.7, fontFamily: 'var(--font-mono)',
  },
  loadingText: { textAlign: 'center', color: 'var(--grey)', padding: '60px', fontFamily: 'var(--font-mono)' },
  emptyState: { textAlign: 'center', padding: '80px 0' },
  emptyIcon: { fontSize: '48px', marginBottom: '16px' },
  emptyText: { fontSize: '16px', color: 'var(--grey)', marginBottom: '24px' },
  table: { background: 'var(--navy-mid)', borderRadius: '12px', overflow: 'hidden', border: '1px solid var(--navy-border)' },
  tableHeader: {
    display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr',
    padding: '14px 24px', background: 'rgba(255,255,255,0.03)',
    fontSize: '11px', fontWeight: '600', letterSpacing: '1.5px',
    color: 'var(--grey)', fontFamily: 'var(--font-mono)',
    borderBottom: '1px solid var(--navy-border)',
  },
  tableRow: {
    display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr',
    padding: '16px 24px', borderBottom: '1px solid var(--navy-border)',
    fontSize: '13px', alignItems: 'center',
  },
  vesselName: { fontWeight: '600', color: 'var(--white)' },
  mono: { fontFamily: 'var(--font-mono)', fontSize: '12px' },
  actions: { display: 'flex', gap: '8px' },
  editBtn: {
    background: 'var(--gold-dim)', border: '1px solid rgba(201,168,76,0.3)',
    color: 'var(--gold)', padding: '5px 12px', borderRadius: '5px',
    fontSize: '11px', cursor: 'pointer', fontWeight: '600',
  },
  deleteBtn: {
    background: 'var(--red-glow)', border: '1px solid rgba(232,0,29,0.3)',
    color: '#ff6b7a', padding: '5px 12px', borderRadius: '5px',
    fontSize: '11px', cursor: 'pointer', fontWeight: '600',
  },
  archBtn: {
    background: 'transparent', border: '1px solid var(--navy-light)',
    color: 'var(--grey)', padding: '6px 16px', borderRadius: '6px',
    fontSize: '11px', letterSpacing: '1px', cursor: 'pointer', fontFamily: 'var(--font-mono)',
  },
  archOverlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000,
  },
  archModal: {
    width: '92vw', height: '90vh', background: '#0d1117',
    borderRadius: '12px', overflow: 'hidden', border: '1px solid var(--navy-border)',
    display: 'flex', flexDirection: 'column',
  },
  archClose: {
    alignSelf: 'flex-end', margin: '8px 12px 0',
    background: 'transparent', border: '1px solid var(--navy-light)',
    color: 'var(--grey)', padding: '4px 14px', borderRadius: '6px',
    fontSize: '11px', cursor: 'pointer', fontFamily: 'var(--font-mono)',
  },
  archFrame: { flex: 1, border: 'none', width: '100%' },
  historyModal: {
    width: '680px', maxHeight: '80vh', background: 'var(--navy-mid)',
    borderRadius: '12px', border: '1px solid var(--navy-border)',
    display: 'flex', flexDirection: 'column', overflow: 'hidden',
  },
  historyHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    padding: '24px 28px', borderBottom: '1px solid var(--navy-border)',
  },
  historyTitle: { fontSize: '15px', fontWeight: '700', letterSpacing: '2px', fontFamily: 'var(--font-mono)', color: 'var(--gold)' },
  historySub: { fontSize: '11px', color: 'var(--grey)', marginTop: '4px' },
  historyBody: { overflowY: 'auto', padding: '8px 0' },
  historyEntry: {
    display: 'flex', gap: '0', borderBottom: '1px solid var(--navy-border)',
  },
  historyEntryLeft: {
    width: '120px', flexShrink: 0, padding: '20px 16px 20px 28px',
    borderRight: '1px solid var(--navy-border)',
  },
  historyVersion: { fontFamily: 'var(--font-mono)', fontSize: '13px', fontWeight: '700', color: 'var(--white)', marginBottom: '4px' },
  historyDate: { fontFamily: 'var(--font-mono)', fontSize: '10px', color: 'var(--grey)' },
  historyEntryRight: { flex: 1, padding: '20px 28px' },
  historyEntryTitle: { fontSize: '13px', fontWeight: '700', color: 'var(--white)', marginBottom: '10px' },
  historyList: { margin: 0, paddingLeft: '16px' },
  historyItem: { fontSize: '12px', color: 'var(--grey)', lineHeight: '1.8' },

  // Agent Collaboration — header button
  collabHeaderBtn: {
    background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.35)',
    color: '#c9a84c', padding: '6px 16px', borderRadius: '6px',
    fontSize: '11px', letterSpacing: '1px', cursor: 'pointer', fontFamily: 'var(--font-mono)',
    fontWeight: '700',
  },
  // Agent Collaboration — modal
  collabModal: {
    width: '92vw', maxWidth: '1100px', maxHeight: '88vh', background: 'var(--navy-mid)',
    borderRadius: '14px', border: '1px solid var(--navy-border)',
    display: 'flex', flexDirection: 'column', overflow: 'hidden',
    boxShadow: '0 24px 80px rgba(0,0,0,0.6)',
  },
  collabModalHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    padding: '24px 28px', borderBottom: '1px solid var(--navy-border)', flexShrink: 0,
  },
  collabModalTitle: { fontSize: '13px', fontWeight: '700', letterSpacing: '2px', fontFamily: 'var(--font-mono)', color: '#c9a84c', marginBottom: '4px' },
  collabModalSub: { fontSize: '13px', color: 'var(--grey)' },
  collabModalBody: { overflowY: 'auto', padding: '24px 28px' },
  collabGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' },
  collabCard: { background: 'rgba(255,255,255,0.02)', border: '1px solid var(--navy-border)', borderRadius: '12px', padding: '20px 22px' },
  collabCardLabel: { fontSize: '9px', fontWeight: '700', letterSpacing: '2px', color: 'var(--grey)', fontFamily: 'var(--font-mono)', marginBottom: '16px', opacity: 0.7 },
  collabFlow: { display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '16px', flexWrap: 'wrap' },
  collabNode: { display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, minWidth: '80px', gap: '6px' },
  collabNodeIcon: { width: '42px', height: '42px', borderRadius: '10px', border: '1px solid', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px' },
  collabNodeName: { fontSize: '12px', fontWeight: '700', color: 'var(--white)', textAlign: 'center' },
  collabNodeDesc: { fontSize: '10px', color: 'var(--grey)', textAlign: 'center', lineHeight: '1.4' },
  collabArrow: { fontSize: '18px', color: 'var(--grey)', opacity: 0.35, flexShrink: 0 },
  collabDesc: { fontSize: '12px', color: 'var(--grey)', lineHeight: '1.7', borderTop: '1px solid var(--navy-border)', paddingTop: '14px' },
};
